import {defineStore} from 'pinia'
import router from "../router/index.js";
import {clearSessionStorage, fetchDevices, fetchGroups, getAuthToken, generateUUID} from "../utils/api.js";
import {remoteAPI} from "../utils/axios.js";
import {UserSchema, AppSchema, DeviceStatusMessageSchema} from "@/schemas";
// import {useRouter} from "vue-router";
// import router from "../router/index.js";

export const useMainStore = defineStore('main', {
    // router: useRouter(),

    state: () => ({
        user: null,
        pairedTVs: [],   // List of { id, name, group, status }
        groups: [],      // List of { id, name, defaultApp, members: tvId[] }
        apps: [],
        pendingCommands: new Map(), // Map of requestId -> { resolve, reject, tvId, timeout }
        websocket: null,
        wsConnected: false
    }),
    actions: {
        startStatusSocket() {
            try {
                // Prevent multiple concurrent connection attempts
                if (this._isConnecting) {
                    console.warn('[STORE] WS connection already in progress, skipping')
                    return
                }

                // Mark as connecting immediately to block concurrent calls
                this._isConnecting = true

                const existing = this.websocket
                if (existing) {
                    // readyState: 0=CONNECTING, 1=OPEN, 2=CLOSING, 3=CLOSED
                    if (existing.readyState === WebSocket.CONNECTING) {
                        console.warn('[STORE] WS already connecting, skipping')
                        this._isConnecting = false
                        return
                    }
                    if (existing.readyState === WebSocket.OPEN) {
                        // send a probe; if it throws, the socket is dead
                        try {
                            existing.send(JSON.stringify({type: 'ping'}))
                            console.warn('[STORE] WS already open')
                            this._isConnecting = false
                            return
                        } catch {
                            this.websocket = null     // drop the zombie
                        }
                    } else {
                        // CLOSING or CLOSED – discard
                        this.websocket = null
                    }
                }

                const token = getToken()
                const baseUrl = import.meta.env.VITE_CHEERIOS_V2_WS_BASE_URL

                const url = `${baseUrl}/remote/socket/site/${this.user.host_site_id}?token=${token}`

                const ws = new WebSocket(url)

                ws.onopen = () => {
                    this._isConnecting = false
                    this._reconnectAttempts = 0
                    console.log('[STORE] WS connected')
                    this.wsConnected = true

                    // Keepalive.
                    this._heartbeatInterval = setInterval(() => {
                        if (ws.readyState === WebSocket.OPEN) {
                            ws.send(JSON.stringify({type: 'ping'}))
                        }
                    }, 50000)
                }

                ws.onclose = ws.onerror = (event) => {
                    this._isConnecting = false
                    console.log(`[STORE] WS disconnected code=${event.code} reason=${event.reason} wasClean=${event.wasClean}`)
                    this.wsConnected = false
                    this.websocket = null
                    clearInterval(this._heartbeatInterval)
                    this._heartbeatInterval = null

                    const token = getAuthToken()
                    if (!token) {
                        console.warn('[STORE] No token. Skipping WS reconnect')
                        return
                    }

                    if (isTokenExpired(token)) {
                        console.warn('[STORE] Token expired. Stopping WS reconnect and logging out')
                        this.logout()
                        return;
                    }

                    const backoff = Math.min(5000 * Math.pow(1.5, (this._reconnectAttempts || 0)), 100000)
                    this._reconnectAttempts = (this._reconnectAttempts || 0) + 1

                    console.log(`[STORE] Reconnecting WS in ${backoff}ms (attempt ${this._reconnectAttempts})`)
                    setTimeout(() => this.startStatusSocket(), backoff)

                    // if (getAuthToken()) setTimeout(() => this.startStatusSocket(), 5000) // auto reconnect
                }

                ws.onmessage = (e) => {
                    try {
                        const rawMsg = JSON.parse(e.data)

                        // Only process ping or device_status messages
                        if (rawMsg.type !== 'ping' && rawMsg.type !== 'device_status') {
                            return
                        }

                        // Parse and validate with schema
                        const msg = DeviceStatusMessageSchema.parse(rawMsg)

                        if (msg.type === 'device_status') {
                            this.updateTvStatus(msg.serial_number, msg.status)
                        }
                        // ping messages are ignored (just for keepalive)
                    } catch (e) {
                        console.error('[STORE] WS message error:', e)
                    }
                }
                this.websocket = ws
            } catch (e) {
                console.error(`[STORE] Unable to start status socket: ${e}`)
            }
        },

        updateTvStatus(serialNumber, status) {
            const tv = this.pairedTVs.find(t => t.serial_number === serialNumber)
            if (tv) {
                tv.status = status
                console.log(`[STORE] Updated ${tv.name} status to ${status}`)
            } else {
                // Unknown device just came online — refresh the device list
                console.log(`[STORE] Unknown device ${serialNumber} reported status, refreshing...`)
                this.refreshSiteData()
            }
        },

        closeStatusSocket() {
            this.websocket?.close()
            this.websocket = null
            this.wsConnected = false
        },

        sendWebSocketCommand(command, params = {}, requestId = null, app = null, serialNumbers = []) {
            if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
                console.warn('[STORE] WebSocket not connected, cannot send command')
                return false
            }

            const message = {
                type: 'command',
                request_id: requestId || generateUUID(),
                command,
                params,
                timestamp: new Date().toISOString(),
            }

            if (app) {
                message.app = app
            }

            if (serialNumbers && serialNumbers.length > 0) {
                message.serial_numbers = serialNumbers
            }

            try {
                this.websocket.send(JSON.stringify(message))
                console.log('[STORE] Sent WS command:', message)
                return true
            } catch (e) {
                console.error('[STORE] Failed to send WS command:', e)
                return false
            }
        },

        registerPendingCommand(requestId, tvId, timeout = 8000) {
            return new Promise((resolve, reject) => {
                const timer = setTimeout(() => {
                    this.pendingCommands.delete(requestId)
                    reject(new Error('Timeout'))
                }, timeout)

                this.pendingCommands.set(requestId, {
                    resolve,
                    reject,
                    tvId,
                    timer
                })
            })
        },

        handleCommandResponse(msg) {
            if (!msg.request_id) return

            const pending = this.pendingCommands.get(msg.request_id)
            if (!pending) {
                // Response arrived before registerPendingCommand was called — buffer briefly
                console.warn('[STORE] Received response for unknown request_id:', msg.request_id)
                return
            }

            clearTimeout(pending.timer)

            if (msg.success) {
                pending.resolve({success: true, tvId: pending.tvId})
            } else if (msg.status === 'busy') {
                // TV reported busy — treat as a silent skip, not a user-facing failure
                pending.resolve({success: true, tvId: pending.tvId, skipped: true})
            } else {
                pending.reject(new Error(msg.error || 'Command failed'))
            }

            this.pendingCommands.delete(msg.request_id)
        },

        cancelPendingCommand(requestId) {
            const pending = this.pendingCommands.get(requestId)
            if (pending) {
                clearTimeout(pending.timer)
                this.pendingCommands.delete(requestId)
            }
        },

        async login(userData) {
            this.user = userData
            console.log('[STORE] Store login, user:', userData)

            await this.loadSiteData(userData.host_site_id)
        },

        async loadSiteData(siteId, {startSocket = true} = {}) {
            console.log('[STORE] Loading data for site:', siteId)
            try {
                // Fetch devices for this site
                console.log('[STORE] Fetching devices...')
                const tvsData = await fetchDevices(siteId)
                console.log('[STORE] Devices loaded:', tvsData)
                // Normalize device status - treat Unknown/null as offline
                this.pairedTVs = (tvsData || []).map(tv => ({
                    ...tv,
                    status: tv.status && tv.status !== 'Unknown' ? tv.status : 'offline'
                }))

                // TODO: Uncomment when groups are implemented
                // // Fetch groups for this site
                // console.log('[STORE] Fetching groups...')
                // const groupsData = await fetchGroups(siteId)
                // console.log('[STORE] Groups loaded:', groupsData)
                // this.groups = groupsData.groups || []

                if (startSocket) this.startStatusSocket()

                console.log('[STORE] Site data loading complete')

            } catch (error) {
                console.error('[STORE] Failed to load site data:', error)
            }
        },

        async logout() {
            await this.clearSession()
            this.closeStatusSocket()
        },

        async restoreSession() {
            console.log('[STORE] Attempting to restore session...')

            try {
                // Verify token is still valid with backend
                const userResponse = await remoteAPI.get('/sites/me')

                const parsedUserResponse = UserSchema.parse(userResponse.data)

                console.log('[STORE] Session restored, user:', parsedUserResponse)

                // Restore user in store (without calling login which would reload data)
                this.user = parsedUserResponse
                return true

            } catch (error) {
                console.warn('[STORE] Session restoration failed:', error)
                if (error.response?.status === 401) await this.logout()
                return false
            }
        },

        async clearSession() {
            console.log('[STORE] Clearing session')
            this.user = null
            this.pairedTVs = []
            this.groups = []
            clearSessionStorage()
            if (router) {
                await router.push('/login')
            }
        },

        addTv(tv) {
            this.pairedTVs.push(tv)
        },

        setGroups(groups) {
            this.groups = groups
        },

        addGroup(group) {
            this.groups.push(group)
        },

        assignTvsToGroup(groupId, tvIds) {
            // Ensure the group exists
            const g = this.groups.find(gr => gr.id === groupId)
            if (g) {
                const unique = new Set([...(g.members || []), ...tvIds])
                g.members = Array.from(unique)
            }
            // Update TVs
            this.pairedTVs.forEach(tv => {
                if (tvIds.includes(tv.id)) tv.group = groupId
            })
        },

        async refreshSiteData() {
            if (!this.user?.host_site_id) {
                console.log('[STORE] No user host_site_id available for refresh')
                return
            }

            console.log('[STORE] Refreshing site data for site:', this.user.host_site_id)
            await this.loadSiteData(this.user.host_site_id, {startSocket: false})
        },

        async loadApps() {
            try {
                const token = getToken()

                const appsResponse = await remoteAPI.get('/sites/me/games')

                const appsData = AppSchema.array().parse(appsResponse.data)

                this.apps = appsData || []
                console.log('[STORE] Apps loaded:', this.apps.length)
                console.log('[STORE] Apps:', this.apps)

            } catch (error) {
                console.error('[STORE] Failed to load apps:', error)
                this.apps = [] // Fallback to empty
            }
        },

        async updateApps() {
            await this.loadApps()
        }
    },

    // getters: {
    //     hasPendingCommands: (state) => state.pendingCommands.size > 0
    // }
})

const getToken = () => {
    const token = getAuthToken()

    if (!token) {
        throw new Error('Missing auth token')
    }

    return token
}

function isTokenExpired(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiry = payload.exp * 1000; // JWT exp is in seconds
        return Date.now() > expiry;
    } catch (e) {
        return true; // malformed token → treat as expired
    }
}